import { useCallback, useMemo } from 'react';
import { renderAppliedFiltersDescription } from '../../../utils/filterUtils';
import useFilters from './useFilters';

const STATUS_FILTER_KEY = 'invoiceStatus';

// Clean up statuses to only include allowed statuses
const getFilteredStatuses = (statuses = [], allowedStatuses = []) =>
    statuses?.filter((s) => allowedStatuses.includes(s));

const useFiltersInvoiceManagementFilters = (pageName, tabValue = 'pre-invoiced') => {
    const { filters, setFilters, filtersSetup, handleApplyFilter, ...rest } = useFilters(pageName);

    const tabFiltersSetup = useMemo(
        () =>
            (filtersSetup ?? []).map((f) => {
                if (f?.name === STATUS_FILTER_KEY) {
                    return {
                        ...f,
                        overrides: {
                            ...(f.overrides || {}),
                            options: [
                                tabValue === 'pre-invoiced' ? 'PreInvoiced' : 'Invoiced',
                                'Errored',
                                'Abandoned',
                                'Outdated',
                            ],
                        },
                    };
                }
                if (f?.name === 'Flags') {
                    return {
                        ...f,
                        overrides: {
                            ...(f.overrides || {}),
                            options: [
                                { label: 'Correction', value: 'CorrectionsOnly' },
                                { label: 'High Priority', value: 'IsHighPriority' },
                            ],
                        },
                    };
                }
                return f;
            }),
        [filtersSetup, tabValue]
    );

    const allowedStatuses = useMemo(
        () => tabFiltersSetup?.find((f) => f?.name === STATUS_FILTER_KEY)?.overrides?.options ?? [],
        [tabFiltersSetup]
    );

    const handleApplyFilterWithValidStatuses = useCallback(
        (newFilters) => {
            if (!Object.prototype.hasOwnProperty.call(newFilters || {}, STATUS_FILTER_KEY)) {
                return handleApplyFilter(newFilters);
            }

            // Keep whatever was previously selected but is NOT visible on this tab
            const disallowedStatuses =
                filters?.[STATUS_FILTER_KEY]?.filter((s) => !allowedStatuses.includes(s)) ?? [];
            const filteredNewStatuses =
                newFilters[STATUS_FILTER_KEY].filter((s) => allowedStatuses.includes(s)) ?? [];

            handleApplyFilter({
                ...newFilters,
                [STATUS_FILTER_KEY]: Array.from(
                    new Set([...disallowedStatuses, ...filteredNewStatuses])
                ),
            });
        },
        [filters, handleApplyFilter, allowedStatuses]
    );

    const filtersWithValidStatuses = useMemo(() => {
        if (!filters?.[STATUS_FILTER_KEY]) return filters;
        return {
            ...filters,
            [STATUS_FILTER_KEY]: getFilteredStatuses(filters[STATUS_FILTER_KEY], allowedStatuses),
        };
    }, [filters, tabFiltersSetup]);

    const appliedFiltersDescription = useMemo(
        () =>
            renderAppliedFiltersDescription(filtersWithValidStatuses, tabFiltersSetup, {
                initialSeparator: '',
            }),
        [filtersWithValidStatuses, tabFiltersSetup]
    );

    return {
        ...rest,
        filters: filtersWithValidStatuses,
        filtersSetup: tabFiltersSetup,
        handleApplyFilter: handleApplyFilterWithValidStatuses,
        appliedFiltersDescription,
    };
};

export default useFiltersInvoiceManagementFilters;
